import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { get, isArray, replace, split, toUpper, trim, every } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
// Models
import { IProductListParams } from '../../partsearch-shared-module/models';
// Service
import { PartSearchService } from '../../partsearch-shared-module/service';
// Constants
import { PLP_DEAFULT_SORT_COLUMN, PLP_DEAFULT_SORT_DIRECTION } from '@constants';
// Transloco
import { TranslocoService } from '@jsverse/transloco';
// Pipe
import { MakeMePrettyPipe } from '@app/core/_base/layout';

export const ProductsListResolverService: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> => {
    const partSearchService = inject(PartSearchService);
    const router = inject(Router);
    const translocoService = inject(TranslocoService);
    const makeMePrettyPipe = inject(MakeMePrettyPipe);

    const payload = getProductsListPayload(route);

    return partSearchService.getProductsList(payload).pipe(
        take(1),
        tap((response: any) => {
            const lang = translocoService.getActiveLang();

            // If the response indicates access is forbidden, redirect to the 403 error page
            if (response?.status === 403) {
                router.navigate(['/', lang, 'error', '403'], { replaceUrl: true });
                throw new Error('Redirecting to error 403 page.');
            }

            // If the response indicates a captcha is required, redirect to the captcha page
            if (response?.status === 422) {
                const returnUrl = state.url !== '/' ? state.url : undefined;
                router.navigate(['/', lang, 'captcha'], {
                    replaceUrl: true,
                    queryParams: {
                        returnUrl: returnUrl
                    }
                });
                throw new Error('Redirecting to captcha page.');
            }

            // If the response contains products, check if they are all dismantling objects and redirect to S10 if true
            const products = get(response, 'data.result', []);
            const sourcePath: string = get(route, 'data.path', 'S1');
            const isDismantlingObjects = every(products, (item: any) => Number(get(item, 'code.part.pc.pc1')) === 107);

            if (sourcePath === 'S1' && isDismantlingObjects) {
                const breadcrumbs = get(response, 'data.seo.html.path');
                const searchLabel = translocoService.translate('label_search');
                const dismantledVehicleLabel = translocoService.translate('label_dismantled_vehicle');
                const vc1 = breadcrumbs?.category?.route?.vc1;
                const vc = breadcrumbs?.category?.route?.vc;
                const vcx = breadcrumbs?.category?.route?.vcx;

                if (!vc1 || !vc || !vcx) return;

                const redirectUrl = [
                    '/',
                    lang,
                    'vs',
                    makeMePrettyPipe.transform(searchLabel),
                    makeMePrettyPipe.transform(dismantledVehicleLabel),
                    's10',
                    vc1,
                    vc,
                    vcx
                ];

                router.navigate(redirectUrl, { replaceUrl: true });
                throw new Error('Redirecting to dismantled vehicle page.');
            }
        }),
        catchError(() => of([]))
    );
};

/**
 * Get Product List (PLP) API Payload based on Routes ie. S1,S3,S15
 * @param {ActivatedRouteSnapshot} route
 * @returns {IProductListParams}
 */
const getProductsListPayload = (route: ActivatedRouteSnapshot): IProductListParams => {
    const sourcePath: string = get(route, 'data.path', 'S1');
    const limit = Number(get(route, 'queryParams.limit', '10'));
    const offset = Number(get(route, 'queryParams.page', '0'));
    const query = trim(get(route, 'queryParams.query', get(route, 'queryParams.q', undefined)));
    const find = trim(get(route, 'queryParams.find', get(route, 'queryParams.f', undefined)));
    const vehicle_post_id = trim(get(route, 'queryParams.vehicle_post_id', undefined));
    const part_type_id = get(route, 'queryParams.part_type_id', []);
    const part_quality_code = get(route, 'queryParams.part_quality_code', []);
    const sort_column = trim(get(route, 'queryParams.sort_column', PLP_DEAFULT_SORT_COLUMN));
    const sort_direction = trim(get(route, 'queryParams.sort_direction', PLP_DEAFULT_SORT_DIRECTION));
    const companyIds = get(route, 'queryParams.company_id', []);
    const companies = isArray(companyIds) ? companyIds : [companyIds];
    const company_id = companies.map(i => Number(i));
    const country_id = get(route, 'queryParams.country_id', []);
    const county_id = get(route, 'queryParams.county_id', []);
    const regno_country = get(route, 'queryParams.country', undefined);
    const regno = get(route, 'queryParams.regno', undefined);
    const group_id = trim(get(route, 'queryParams.group_id', undefined));

    // Handle position filtering
    const position_filter = get(route, 'queryParams.position', []);
    const positions = isArray(position_filter) ? position_filter : [position_filter];
    const position = get(route, 'params.pos');
    const pos = positions.length > 0 ? positions : (position ? split(position, '-') : []);

    const from_model_year = get(route, 'queryParams.from_model_year', undefined);
    const to_model_year = get(route, 'queryParams.to_model_year', undefined);
    const from_milage_km = get(route, 'queryParams.from_milage_km', undefined);
    const to_milage_km = get(route, 'queryParams.to_milage_km', undefined);

    const params: any = {
        limit,
        offset: limit * offset,
        code_vc_make_model: get(route, 'params.code_vc_make_model'),
        code_vc_myft: get(route, 'params.code_vc_myft'),
        pc1: get(route, 'params.pc1'),
        pc: get(route, 'params.pc'),
        vt1: get(route, 'params.vt1'),
        pos,
        query: (sourcePath === 'S11') ? undefined : query,
        find,
        part_type_id: isArray(part_type_id) ? part_type_id : [part_type_id],
        part_quality_code: isArray(part_quality_code) ? part_quality_code : [part_quality_code],
        country_id: isArray(country_id) ? country_id : [country_id],
        county_id: isArray(county_id) ? county_id : [county_id],
        sort_column: sort_column === '' ? PLP_DEAFULT_SORT_COLUMN : sort_column,
        sort_direction: sort_direction === '' ? PLP_DEAFULT_SORT_DIRECTION : sort_direction,
        company_id,
        group_id,
        regno: (regno_country && regno) ? `${regno_country}-${regno}` : undefined,
        vehicle_post_id: vehicle_post_id ? [vehicle_post_id] : undefined,
        from_model_year,
        to_model_year,
        from_milage_km,
        to_milage_km
    };

    switch (sourcePath) {
        case 'S1':
            return { ...params, sm: 1, path: '/{lang}/pb/{label_search}/{label_car_parts}/s1/{vc1}/{vc}/{vcx}/{pc1}/{pc}/{pos}' };
        case 'OV':
            return { ...params, sm: 1, path: '/{lang}/ov/{label_search}/{label_car_parts}/{vc1}/{vc}/{vcx}/{pc1}/{pc}/{pos}' };
        case 'S3':
            const oem = get(route, 'params.oem');
            return oem ? {
                ...params, sm: 3,
                query: trim(decodeURIComponent(toUpper(oem))).replace(/[{},@!`*]/g, ''),
                exact: get(route, 'queryParams.exact', '0') === '1' ? 1 : 0,
                path: `/{lang}/OEM/{oem}`
            } : {
                ...params, sm: 3,
                exact: get(route, 'queryParams.exact', '0') === '1' ? 1 : 0,
                path: `/{lang}/pb/{label_search}/{label_car_parts}/s3?query=${toUpper(query).replace(/[{},@!`*]/g, '')}`
            };
        case 'S6':
            return { ...params, sm: 6, path: `/{lang}/pb/{label_search}/{label_car_parts}/s6/{pc1}/{pc}/{pos}?query=${toUpper(query).replace(/[{},@!`*]/g, '')}` };
        case 'S7':
            return { ...params, sm: 7, path: `/{lang}/pb/{label_search}/{label_car_parts}/s7/{pc1}/{pc}/{pos}?query=${toUpper(query).replace(/[{},@!`*]/g, '')}` };
        case 'S9':
            return { ...params, sm: 9, path: `/{lang}/pb/{label_search}/{label_car_parts}/s9/AP/{pc1}/{pc}/{pos}` };
        case 'S11':
            const partitem = get(route, 'params.partitem');
            return partitem ? {
                ...params, sm: 11,
                query: toUpper(partitem).replace(/\D+/g, '').trim(),
                path: `/{lang}/part-item/${partitem}`
            } : {
                ...params, sm: 11,
                query: toUpper(query).replace(/\D+/g, '').trim(),
                path: `/{lang}/pb/{label_search}/{label_car_parts}/s11?query=${query}`
            };
        case 'S15':
            return { ...params, sm: 3, query, path: `/{lang}/pb/{label_search}/{label_car_parts}/s15/FTS?query=${query}` };
        case 'S10':
            return { ...params, sm: 10, path: `/{lang}/pb/{label_search}/{label_car_parts}/s10/{vc1}/{vc}/{vcx}?vehicle_post_id=${vehicle_post_id}` };
        case 'S17':
            return { ...params, sm: 17, path: '/{lang}/pb/{label_search}/{label_car_parts}/s17/{vc1}/a/a/{pc1}/{pc}/{pos}' };
        case 'S19':
            return { ...params, sm: 19, vt2: get(route, 'params.vt2'), path: '/{lang}/pb/{label_search}/{label_car_parts}/s19/{vc1}/{vc2}/{pc1}/{pc}/{pos}' };
    }
    return params;
};
